import React, { useState, useEffect } from 'react';
import { Trash2, Receipt, Calendar, Plus, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import SearchableMedicineDropdownByStock from '../../components/SearchableMedicineDropdownByStock';

const safeToFixed = (value, digits = 2) => {
    const num = Number(value);
    return Number.isFinite(num) ? Number(num.toFixed(digits)) : 0;
};

const EditInvoice = () => {
    const navigate = useNavigate();
    const { invoiceId } = useParams();
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [medicines, setMedicines] = useState([]);
    const [medicineList, setMedicineList] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [batchNumbers, setBatchNumbers] = useState({});
    const [selectedDoctor, setSelectedDoctor] = useState('');
    const [isManualRound, setIsManualRound] = useState(false);
    const [focusRowId, setFocusRowId] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        mobileNumber: '',
        village: '',
        netAmount: '',
        paymentMode: '',
        cashAmount: '',
        onlineAmount: '',
        debitsAmount: '',
        round_amount: '0',
    });
    const [errors, setErrors] = useState({});

    // Fetch Medicines
    useEffect(() => {
        const fetchMedicines = async () => {
            try {
                const response = await axiosInstance.get('/medicine');
                setMedicineList(response.data.medicines || []);
            } catch (error) {
                console.error('Error fetching medicines:', error);
                setMedicineList([]);
            }
        };

        fetchMedicines();
    }, []);

    // Fetch Invoice Data
    useEffect(() => {
        if (!invoiceId) return;

        const fetchInvoice = async () => {
            try {
                const response = await axiosInstance.get(`/invoice/${invoiceId}`);
                const inv = response.data;

                // Set form data
                setFormData({
                    name: inv.name || '',
                    mobileNumber: inv.mobile_number || '',
                    village: inv.village || '',
                    netAmount: inv.net_amount?.toString() || '',
                    paymentMode: inv.payment_mode || '',
                    cashAmount: inv.cash_amount?.toString() || '',
                    onlineAmount: inv.online_amount?.toString() || '',
                    debitsAmount: inv.debits_amount?.toString() || '',
                    round_amount: inv.round_amount?.toString() || '0',
                });

                setSelectedDoctor(inv.doctor || '');
                // Set invoice date if provided by API (normalize to YYYY-MM-DD)
                if (inv.invoice_date) {
                    try {
                        const d = new Date(inv.invoice_date);
                        if (!isNaN(d)) {
                            setInvoiceDate(d.toISOString().split('T')[0]);
                        }
                    } catch (e) {
                        // fallback: if it's already YYYY-MM-DD
                        setInvoiceDate(inv.invoice_date);
                    }
                }

                // Transform invoice items with medicine data from response
                const loadedMedicines = (inv.InvoiceItems || []).map((item, idx) => ({
                    id: Date.now() + idx,
                    invoiceItemId: item.id,
                    medicine: item.medicine_id.toString(),
                    medicineName: item.medicines?.name || '', // Store medicine name from response
                    batchNumber: item.batch_number || '',
                    quantity: item.quantity || 0,
                    basePrice: parseFloat(item.base_price) || 0,
                    pricePerUnit: parseFloat(item.price_per_unit) || 0,
                    gstPercentage: parseFloat(item.gst_percentage) || 0,
                    gstAmount: parseFloat(item.gst_amount) || 0,
                    discountPercentage: parseFloat(item.discount_percentage) || 0,
                    discountAmount: parseFloat(item.discount_amount) || 0,
                    totalPrice: parseFloat(item.total_price) || 0,
                    availableStock: 0,
                    expiryDate: item.expiry_date || '',
                    returnedQuantity: item.returned_quantity || 0,
                    netQuantity: (item.net_quantity !== undefined && item.net_quantity !== null) ? item.net_quantity : (item.quantity || 0),
                    hasReturns: !!item.has_returns,
                    isExisting: true, // Mark as existing medicine
                }));

                setMedicines(loadedMedicines);

                // Fetch batches for each unique medicine to get stock info
                const uniqueMedicineIds = [...new Set(loadedMedicines.map(m => m.medicine))];
                uniqueMedicineIds.forEach(medId => {
                    if (medId) fetchBatches(medId);
                });

            } catch (error) {
                console.error('Error loading invoice:', error);
                setErrors(prev => ({ ...prev, submit: 'Failed to load invoice' }));
            }
        };

        fetchInvoice();
    }, [invoiceId]);

    // Calculate Total Amount
    // useEffect(() => {
    //   const subtotal = medicines.reduce((sum, med) => sum + ((med.basePrice || 0) * (med.quantity || 0)), 0);
    //   const totalGst = medicines.reduce((sum, med) => sum + (med.gstAmount || 0), 0);
    //   const totalDiscount = medicines.reduce((sum, med) => sum + (med.discountAmount || 0), 0);
    //   const totalAmt = parseFloat((subtotal + totalGst - totalDiscount).toFixed(2));
    //   setTotalAmount(totalAmt);
    //   if (!formData.netAmount || formData.netAmount === '0') {
    //     setFormData(prev => ({ ...prev, netAmount: totalAmt.toFixed(2) }));
    //   }
    // }, [medicines, formData.netAmount]);

    useEffect(() => {
        const subtotal = medicines.reduce((sum, med) => sum + ((med.basePrice || 0) * (med.quantity || 0)), 0);
        const totalGst = medicines.reduce((sum, med) => sum + (med.gstAmount || 0), 0);
        const totalDiscount = medicines.reduce((sum, med) => sum + (med.discountAmount || 0), 0);
        const totalAmt = parseFloat((subtotal + totalGst - totalDiscount).toFixed(2));

        setTotalAmount(totalAmt);

        if (!isManualRound) {
            // ✅ Custom rounding logic: nearest 10 based on last digit
            const lastDigit = totalAmt % 10;  // Get decimal part after tens place
            let roundedNet;

            if (lastDigit >= 5) {
                // Round up to next 10
                roundedNet = Math.ceil(totalAmt / 10) * 10;
            } else {
                // Round down to previous 10
                roundedNet = Math.floor(totalAmt / 10) * 10;
            }

            const adjustment = parseFloat((roundedNet - totalAmt).toFixed(2));

            setFormData(prev => ({
                ...prev,
                netAmount: roundedNet.toFixed(2),
                round_amount: adjustment.toFixed(2)
            }));
        }

        // Fix stock 0 issue
        medicines.forEach(med => {
            if (med.medicine && med.batchNumber && batchNumbers[med.medicine]) {
                const batch = batchNumbers[med.medicine].find(b => b.batch_number === med.batchNumber);
                if (batch && med.availableStock === 0) {
                    setMedicines(prev => prev.map(m =>
                        m.id === med.id
                            ? { ...m, availableStock: batch.available_units || 0, expiryDate: batch.expiry_date || '' }
                            : m
                    ));
                }
            }
        });
    }, [medicines, batchNumbers, isManualRound]);

    // Helper: Calculate row data from batch
    const calculateRowFromBatch = (med, batch) => {
        const pricePerUnit = parseFloat(batch.selling_price_per_unit) || 0;
        const quantity = med.quantity || 0;
        const subtotal = safeToFixed(quantity * pricePerUnit);
        const discountAmount = safeToFixed(subtotal * ((med.discountPercentage || 0) / 100));
        const totalPrice = safeToFixed(subtotal - discountAmount);

        return {
            ...med,
            batchNumber: batch.batch_number,
            pricePerUnit,
            basePrice: pricePerUnit,
            gstAmount: 0,
            discountAmount,
            totalPrice,
            availableStock: batch.available_units || 0,
            expiryDate: batch.expiry_date || '',
        };
    };

    const fetchBatches = async (medicineId, rowId) => {
        try {
            const response = await axiosInstance.get(`/stock/${medicineId}`);
            const batches = response.data.batches || [];
            setBatchNumbers(prev => ({ ...prev, [medicineId]: batches }));

            // Auto-focus and Auto-select logic
            if (rowId) {
                // Focus Batch Dropdown
                setTimeout(() => {
                    const batchSelect = document.getElementById(`batch-${rowId}`);
                    if (batchSelect) batchSelect.focus();
                }, 100);

                // Auto-select if single batch
                if (batches.length === 1) {
                    const batch = batches[0];
                    setMedicines(prev => prev.map(med => {
                        if (med.id === rowId) {
                            return calculateRowFromBatch(med, batch);
                        }
                        return med;
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching batches:', error);
        }
    };

    // const handleBatchChange = (id, batchNumber, medicineId) => {
    //   const selectedBatch = batchNumbers[medicineId]?.find(b => b.batch_number === batchNumber);
    //   const selectedMedicine = medicineList.find(m => m.id === parseInt(medicineId));

    //   if (selectedBatch && selectedMedicine) {
    //     const gstPercentage = selectedMedicine.gst_percentage || 0;
    //     const sellingPrice = parseFloat(selectedBatch.selling_price_per_unit) || 0;
    //     const basePrice = safeToFixed(sellingPrice / (1 + gstPercentage / 100));

    //     setMedicines(prev => prev.map(med => {
    //       if (med.id === id) {
    //         const quantity = med.quantity || 0;
    //         const baseAmount = safeToFixed(basePrice * quantity);
    //         const gstAmount = safeToFixed(baseAmount * (gstPercentage / 100));
    //         const discountAmount = safeToFixed((baseAmount + gstAmount) * ((med.discountPercentage || 0) / 100));
    //         const finalPrice = safeToFixed(baseAmount + gstAmount - discountAmount);

    //         return {
    //           ...med,
    //           batchNumber,
    //           basePrice,
    //           pricePerUnit: sellingPrice,
    //           gstPercentage,
    //           gstAmount,
    //           discountAmount,
    //           availableStock: selectedBatch.available_units || 0,
    //           expiryDate: selectedBatch.expiry_date || '',
    //           totalPrice: finalPrice,
    //         };
    //       }
    //       return med;
    //     }));
    //   }
    // };

    const handleEnterKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleKeyPress = (e, type, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            if (type === 'medicine') {
                // Focus Batch Dropdown
                setTimeout(() => {
                    const batchSelect = document.getElementById(`batch-${medicines[index].id}`);
                    if (batchSelect) batchSelect.focus();
                }, 100);
            } else if (type === 'batch') {
                // Focus Quantity
                const qtyInput = document.getElementById(`qty-${medicines[index].id}`);
                if (qtyInput) qtyInput.focus();
            } else if (type === 'quantity') {
                // Focus Add Button
                const addButton = document.getElementById('add-medicine-btn');
                if (addButton) addButton.focus();
            } else if (type === 'add') {
                addMedicine();
            }
        }
    };

    const handleBatchChange = (id, batchNumber, medicineId) => {
        const selectedBatch = batchNumbers[medicineId]?.find(b => b.batch_number === batchNumber);
        if (!selectedBatch) return;

        setMedicines(prev => prev.map(med => {
            if (med.id === id) {
                return calculateRowFromBatch(med, selectedBatch);
            }
            return med;
        }));
    };

    const addMedicine = () => {
        if (medicines.some(med => !isRowValid(med))) {
            setErrors(prev => ({ ...prev, medicines: 'Please complete the current row first' }));
            return;
        }
        const newId = Date.now() + Math.random();
        const newMedicine = {
            id: newId,
            medicine: '',
            batchNumber: '',
            expiryDate: '',
            quantity: 0,
            basePrice: 0,
            pricePerUnit: 0,
            gstPercentage: 0,
            gstAmount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            totalPrice: 0,
            availableStock: 0,
            isExisting: false, // Mark as new medicine
        };
        setMedicines([...medicines, newMedicine]);
        setFocusRowId(newId);
        setErrors(prev => ({ ...prev, medicines: '' }));
    };

    const handleMedicineChange = (id, field, value) => {
        const updatedMedicines = medicines.map(med => {
            if (med.id === id) {
                const updatedMed = { ...med, [field]: value };
                if (field === 'medicine') {
                    const medicineId = typeof value === 'object' ? value.id : value;
                    const currentMedicineId = med.medicine;

                    // Only fetch batches if medicine is actually changing (not initial load)
                    // For new medicines (!isExisting), always fetch when medicine is selected
                    // For existing medicines (isExisting), only fetch if user is changing the selection
                    const shouldFetchBatches = !med.isExisting || (med.isExisting && currentMedicineId && String(currentMedicineId) !== String(medicineId));

                    if (shouldFetchBatches) {
                        fetchBatches(medicineId, med.id);
                    }

                    const selectedMedicine = typeof value === 'object' ? value : medicineList.find(m => m.id === parseInt(value));

                    // Only reset fields if medicine is actually changing
                    if (String(currentMedicineId) !== String(medicineId)) {
                        Object.assign(updatedMed, {
                            medicine: medicineId,
                            batchNumber: '',
                            basePrice: 0,
                            pricePerUnit: 0,
                            gstPercentage: selectedMedicine?.gst_percentage || 0,
                            gstAmount: 0,
                            discountPercentage: 0,
                            discountAmount: 0,
                            availableStock: 0,
                            expiryDate: '',
                            totalPrice: 0,
                        });
                    } else {
                        // Just update the medicine ID without clearing other fields
                        updatedMed.medicine = medicineId;
                    }
                }
                if (['quantity', 'gstPercentage', 'discountPercentage'].includes(field)) {
                    const quantity = field === 'quantity' ? value : updatedMed.quantity;
                    const totalBasePrice = safeToFixed((updatedMed.basePrice || 0) * (quantity || 0));
                    const gstAmount = safeToFixed(totalBasePrice * ((updatedMed.gstPercentage || 0) / 100));
                    const discountAmount = safeToFixed((totalBasePrice + gstAmount) * ((updatedMed.discountPercentage || 0) / 100));
                    updatedMed.gstAmount = gstAmount;
                    updatedMed.discountAmount = discountAmount;
                    updatedMed.totalPrice = safeToFixed(totalBasePrice + gstAmount - discountAmount);
                }
                return updatedMed;
            }
            return med;
        });
        setMedicines(updatedMedicines);
    };

    const removeMedicine = async (id) => {
        const medicineToRemove = medicines.find(med => med.id === id);

        // Show confirmation dialog
        const confirmDelete = window.confirm(
            `Are you sure you want to remove this medicine?\n${medicineToRemove?.medicineName || 'Medicine'}`
        );

        if (!confirmDelete) return;

        // If medicine has invoiceItemId, call delete API
        if (medicineToRemove?.invoiceItemId) {
            try {
                await axiosInstance.delete(`/invoice/item/${medicineToRemove.invoiceItemId}`);
                alert('Medicine deleted successfully!');
            } catch (error) {
                console.error('Error deleting invoice item:', error);
                alert('Failed to delete medicine item. Please try again.');
                return;
            }
        }

        // Remove from state
        setMedicines(medicines.filter(med => med.id !== id));
    };

    // const handleChange = (e) => {
    //   const { name, value } = e.target;
    //   setFormData(prev => ({ ...prev, [name]: value }));
    //   if (name === 'paymentMode') {
    //     setFormData(prev => ({ ...prev, cashAmount: '', onlineAmount: '', debitsAmount: '' }));
    //   }
    //   setErrors(prev => ({ ...prev, [name]: '' }));
    // };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'round_amount') {
            setIsManualRound(true);
            const adj = parseFloat(value) || 0;
            const newNetAmount = (totalAmount + adj).toFixed(2);

            setFormData(prev => ({
                ...prev,
                round_amount: value,
                netAmount: newNetAmount
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (name === 'paymentMode') {
            setFormData(prev => ({ ...prev, cashAmount: '', onlineAmount: '', debitsAmount: '' }));
        }
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = 'Name is required';
        // if (!formData.mobileNumber?.trim()) newErrors.mobileNumber = 'Mobile Number is required';
        if (formData.mobileNumber && !/^\d{10}$/.test(formData.mobileNumber)) {
            newErrors.mobileNumber = 'Mobile Number must be 10 digits';
        }
        if (!formData.paymentMode) newErrors.paymentMode = 'Payment Mode is required';
        if (!formData.netAmount || parseFloat(formData.netAmount) <= 0) {
            newErrors.netAmount = 'Net Amount must be positive';
        }
        if (formData.paymentMode === 'cash_and_online') {
            if (!formData.cashAmount || parseFloat(formData.cashAmount) < 0) newErrors.cashAmount = 'Cash Amount required';
            if (!formData.onlineAmount || parseFloat(formData.onlineAmount) < 0) newErrors.onlineAmount = 'Online Amount required';
        }
        if (formData.paymentMode === 'debits') {
            if (!formData.debitsAmount || parseFloat(formData.debitsAmount) <= 0) newErrors.debitsAmount = 'Debits Amount required';
        }
        // medicines.forEach((med, i) => {
        //   if (med.quantity > med.availableStock) {
        //     newErrors[`medicine_${i}`] = `Qty exceeds stock (${med.availableStock})`;
        //   }
        // });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isRowValid = (med) => {
        return med.medicine && med.batchNumber && med.quantity > 0 && med.pricePerUnit > 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || !medicines.every(isRowValid)) return;

        const invoiceData = {
            village: formData.village || formData.mobileNumber,
            name: formData.name,
            mobile_number: formData.mobileNumber,
            doctor: selectedDoctor || null,
            invoice_date: invoiceDate,
            total_amount: totalAmount,
            discount: 0,
            tax: 0,
            net_amount: parseFloat(formData.netAmount),
            payment_mode: formData.paymentMode,
            cash_amount: formData.paymentMode === 'cash_and_online' ? parseFloat(formData.cashAmount) : null,
            online_amount: formData.paymentMode === 'cash_and_online' ? parseFloat(formData.onlineAmount) : null,
            debits_amount: formData.paymentMode === 'debits' ? parseFloat(formData.debitsAmount) : null,
            remark: formData.paymentMode === 'debits' ? 'baki' : '',
            items: medicines.map(med => ({
                id: med.invoiceItemId || undefined,
                medicine_id: parseInt(med.medicine),
                batch_number: med.batchNumber,
                quantity: parseInt(med.quantity),
                base_price: safeToFixed(med.basePrice),
                price_per_unit: safeToFixed(med.pricePerUnit),
                gst_percentage: safeToFixed(med.gstPercentage),
                gst_amount: safeToFixed(med.gstAmount),
                discount_percentage: safeToFixed(med.discountPercentage),
                discount_amount: safeToFixed(med.discountAmount),
                total_price: safeToFixed(med.totalPrice),
            })),
        };

        try {
            await axiosInstance.put(`/invoice/${invoiceId}`, invoiceData);
            alert('Invoice updated successfully!');
            navigate('/invoice');
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                submit: error.response?.data?.message || 'Failed to update invoice'
            }));
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-4 px-3 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-teal-100">
                        <div className="bg-teal-600 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl">
                                        <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl sm:text-2xl font-bold text-white">Edit Invoice</h1>
                                        <p className="text-teal-100 text-xs sm:text-sm mt-1">Update existing sale invoice</p>
                                    </div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-3 border border-white/20 shadow-lg transition-all duration-300 hover:border-teal-300/50 hover:shadow-teal-600/30">
                                    <label className="text-xs sm:text-sm font-medium text-teal-100 tracking-wide">
                                        Invoice Date
                                    </label>
                                    <div className="relative mt-2">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-300 pointer-events-none" />
                                        <input
                                            type="date"
                                            value={invoiceDate}
                                            onChange={(e) => setInvoiceDate(e.target.value)}
                                            onKeyDown={handleEnterKey}
                                            className="w-full bg-transparent text-white font-semibold text-sm sm:text-base pl-10 pr-4 py-2 rounded-xl border border-white/20 outline-none transition-all duration-300 focus:border-teal-300 focus:shadow-lg focus:shadow-teal-500/20 cursor-pointer" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {errors.submit && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                                <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
                            </div>
                        )}

                        {/* Customer Details */}
                        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 flex items-center">
                                <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                Customer Details
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="Customer name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile Number</label>
                                    <input
                                        type="tel"
                                        name="mobileNumber"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="10-digit number"
                                        value={formData.mobileNumber}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                    {errors.mobileNumber && <p className="text-red-500 text-xs mt-1.5">{errors.mobileNumber}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Village Name</label>
                                    <input
                                        type="text"
                                        name="village"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 hover:border-teal-400 transition-all"
                                        placeholder="Enter Village Name"
                                        value={formData.village}
                                        onChange={handleChange}
                                        onKeyDown={handleEnterKey}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Doctor</label>
                                    <select
                                        value={selectedDoctor}
                                        onChange={(e) => setSelectedDoctor(e.target.value)}
                                        onKeyDown={handleEnterKey}
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                    >
                                        <option value="">Select Doctor</option>
                                        <option value="Dr. Ashraf Sherasiya">Dr. Ashraf Sherasiya</option>
                                        <option value="Dr. Rozmeen Sherasiya">Dr. Rozmeen Sherasiya</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Medicines Section */}
                        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                            <div className="mb-4">
                                <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Medicines
                                </h2>
                            </div>

                            {errors.medicines && (
                                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-lg mb-4">
                                    <p className="text-red-700 text-sm">{errors.medicines}</p>
                                </div>
                            )}

                            {/* Desktop View */}
                            <div className="hidden lg:block space-y-5">
                                {medicines.map((med, index) => {
                                    const medicineName = med.medicineName || medicineList.find(m => m.id === parseInt(med.medicine))?.name || 'Select Medicine';
                                    const isComplete = med.medicine && med.batchNumber && med.quantity > 0 && med.pricePerUnit > 0;

                                    return (
                                        <div
                                            key={med.id}
                                            className={`relative bg-white rounded-2xl shadow-xl border-2 p-6 transition-all duration-300 hover:shadow-2xl ${isComplete
                                                ? 'border-teal-400 ring-2 ring-teal-100 ring-opacity-50'
                                                : 'border-gray-200'
                                                }`}
                                        >
                                            {/* Header: Medicine Name + Delete */}
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-teal-500 text-white font-bold rounded-full flex items-center justify-center shadow-md">
                                                        {index + 1}
                                                    </div>
                                                    <h3 className="text-lg font-bold text-gray-800">{medicineName}</h3>
                                                    {isComplete && (
                                                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full animate-pulse">
                                                            Ready
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMedicine(med.id)}
                                                    className="p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all transform hover:scale-110"
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                </button>
                                            </div>

                                            {/* Grid Layout - 4 Columns */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 text-sm">
                                                <div className="xl:col-span-1">
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Medicine *</label>
                                                    {med.isExisting ? (
                                                        <select
                                                            value={med.medicine}
                                                            onChange={(e) => handleMedicineChange(med.id, 'medicine', e.target.value)}
                                                            disabled
                                                            className="w-full px-3 py-2.5 text-sm font-medium bg-gray-100 border border-gray-300 rounded-xl cursor-not-allowed text-gray-600"
                                                        >
                                                            <option value="">Select Medicine</option>
                                                            {/* Show loaded medicine name if not in list yet */}
                                                            {med.isExisting && med.medicine && med.medicineName && !medicineList.find(m => m.id === parseInt(med.medicine)) && (
                                                                <option value={med.medicine}>{med.medicineName}</option>
                                                            )}
                                                            {medicineList.map(m => (
                                                                <option key={m.id} value={m.id}>
                                                                    {m.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <SearchableMedicineDropdownByStock
                                                            value={med.medicine}
                                                            onChange={(medicine) => handleMedicineChange(med.id, 'medicine', medicine)}
                                                            onKeyDown={(e) => handleKeyPress(e, 'medicine', index)}
                                                            autoFocus={med.id === focusRowId}
                                                            placeholder="Search medicine..."
                                                            className="w-full"
                                                        />
                                                    )}
                                                </div>

                                                {/* Batch */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Batch No.</label>
                                                    <select
                                                        id={`batch-${med.id}`}
                                                        value={med.batchNumber}
                                                        onChange={(e) => handleBatchChange(med.id, e.target.value, med.medicine)}
                                                        onKeyDown={(e) => handleKeyPress(e, 'batch', index)}
                                                        disabled={!med.medicine || med.isExisting}
                                                        className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                                                    >
                                                        <option value="">Select Batch</option>
                                                        {/* Show loaded batch if it exists and not in list yet */}
                                                        {med.isExisting && med.batchNumber && !batchNumbers[med.medicine]?.find(b => b.batch_number === med.batchNumber) && (
                                                            <option value={med.batchNumber}>{med.batchNumber}</option>
                                                        )}
                                                        {batchNumbers[med.medicine]?.map(batch => (
                                                            <option key={batch.batch_number} value={batch.batch_number}>
                                                                {batch.batch_number}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Expiry + Stock */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry</label>
                                                    <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 font-medium">
                                                        {med.expiryDate ? new Date(med.expiryDate).toLocaleDateString('en-IN') : '-'}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Stock</label>
                                                    <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl text-center">
                                                        {med.availableStock || 0}
                                                    </div>
                                                </div>

                                                {/* Quantity */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Qty *</label>
                                                    <input
                                                        id={`qty-${med.id}`}
                                                        type="number"
                                                        min="1"
                                                        value={med.quantity}
                                                        onChange={(e) => handleMedicineChange(med.id, 'quantity', parseInt(e.target.value) || 0)}
                                                        onKeyDown={(e) => handleKeyPress(e, 'quantity', index)}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0"
                                                    />
                                                    {errors[`medicine_${index}`] && (
                                                        <p className="text-xs text-red-500 mt-1">{errors[`medicine_${index}`]}</p>
                                                    )}
                                                    {med.hasReturns && (
                                                        <div className="mt-2 text-xs">
                                                            <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 font-bold rounded-full mr-2">-{med.returnedQuantity} Returned</span>
                                                            <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 font-bold rounded-full">= {med.netQuantity} Net</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Price Per Unit */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Price/Unit</label>
                                                    <div className="px-3 py-2.5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl font-bold text-emerald-700">
                                                        ₹{parseFloat(med.pricePerUnit || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                {/* GST % & Amount */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">GST %</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={med.gstPercentage}
                                                        onChange={(e) => handleMedicineChange(med.id, 'gstPercentage', parseFloat(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                        placeholder="0"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">GST ₹</label>
                                                    <div className="px-3 py-2.5 bg-teal-100 text-teal-700 font-bold rounded-xl">
                                                        ₹{(med.gstAmount || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                {/* Discount % & Amount */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Disc %</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={med.discountPercentage}
                                                        onChange={(e) => handleMedicineChange(med.id, 'discountPercentage', parseFloat(e.target.value) || 0)}
                                                        onKeyDown={handleEnterKey}
                                                        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                        placeholder="0"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Disc ₹</label>
                                                    <div className="px-3 py-2.5 bg-orange-100 text-orange-700 font-bold rounded-xl">
                                                        ₹{(med.discountAmount || 0).toFixed(2)}
                                                    </div>
                                                </div>

                                                {/* Total Price - Highlighted */}
                                                <div className="xl:col-span-2">
                                                    <label className="block textś-xs font-semibold text-gray-600 mb-1.5">Total Price</label>
                                                    <div className="px-5 py-3 bg-teal-600 text-white font-bold text-lg rounded-xl text-center shadow-md">
                                                        ₹{(med.totalPrice || 0).toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Visual Indicator */}
                                            {!isComplete && (
                                                <div className="mt-4 flex items-center gap-2 text-amber-600 text-xs">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>Please complete all fields</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="flex justify-end">
                                    <button
                                        id="add-medicine-btn"
                                        type="button"
                                        onClick={addMedicine}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addMedicine();
                                            }
                                        }}
                                        className="flex items-center space-x-2 px-4 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="font-semibold">Add Medicine</span>
                                    </button>
                                </div>
                            </div>

                            {/* Mobile View */}
                            <div className="lg:hidden space-y-4">
                                {medicines.map((med, index) => {
                                    const medicineName = med.medicineName || medicineList.find(m => m.id === parseInt(med.medicine))?.name || 'Select Medicine';
                                    return (
                                        <div key={med.id} className="bg-gradient-to-br from-white to-teal-50/30 rounded-xl border-2 border-teal-100 overflow-hidden shadow-md">
                                            <div className="bg-teal-600 p-3 flex justify-between items-center">
                                                <h3 className="text-white font-bold text-sm">Medicine #{index + 1}</h3>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMedicine(med.id)}
                                                    className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="h-4 w-4 text-white" />
                                                </button>
                                            </div>
                                            <div className="p-4 space-y-3">
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Medicine</label>
                                                    {med.isExisting ? (
                                                        <select
                                                            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 bg-gray-100 cursor-not-allowed text-gray-600 text-sm"
                                                            value={med.medicine}
                                                            onChange={(e) => handleMedicineChange(med.id, 'medicine', e.target.value)}
                                                            onKeyDown={(e) => handleKeyPress(e, 'medicine', index)}
                                                            disabled={med.isExisting}
                                                        >
                                                            <option value="">Select Medicine</option>
                                                            {/* Show loaded medicine name if not in list yet */}
                                                            {med.isExisting && med.medicine && med.medicineName && !medicineList.find(m => m.id === parseInt(med.medicine)) && (
                                                                <option value={med.medicine}>{med.medicineName}</option>
                                                            )}
                                                            {medicineList.map(m => (
                                                                <option key={m.id} value={m.id}>{m.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <SearchableMedicineDropdownByStock
                                                            value={med.medicine}
                                                            onChange={(medicine) => handleMedicineChange(med.id, 'medicine', medicine)}
                                                            onKeyDown={(e) => handleKeyPress(e, 'medicine', index)}
                                                            placeholder="Search medicine..."
                                                            className="w-full"
                                                        />
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Batch</label>
                                                        <select
                                                            value={med.batchNumber}
                                                            onChange={(e) => handleBatchChange(med.id, e.target.value, med.medicine)}
                                                            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all text-sm"
                                                            disabled={!med.medicine}
                                                        >
                                                            <option value="">Select Batch</option>
                                                            {/* Show loaded batch if it exists and not in list yet */}
                                                            {med.isExisting && med.batchNumber && !batchNumbers[med.medicine]?.find(b => b.batch_number === med.batchNumber) && (
                                                                <option value={med.batchNumber}>{med.batchNumber}</option>
                                                            )}
                                                            {batchNumbers[med.medicine]?.map(batch => (
                                                                <option key={batch.batch_number} value={batch.batch_number}>
                                                                    {batch.batch_number}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Expiry</label>
                                                        <input
                                                            type="text"
                                                            value={med.expiryDate ? med.expiryDate.split('T')[0] : '-'}
                                                            readOnly
                                                            className="w-full px-3 py-2 rounded-lg bg-gray-50 text-gray-600 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Stock</label>
                                                        <div className="px-3 py-2 text-center bg-teal-100 text-teal-700 font-bold rounded-lg text-sm">
                                                            {med.availableStock || 0}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">Qty</label>
                                                        <input
                                                            type="number"
                                                            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-teal-500 text-sm"
                                                            value={med.quantity}
                                                            onChange={(e) => handleMedicineChange(med.id, 'quantity', parseInt(e.target.value) || 0)}
                                                            onKeyDown={(e) => handleKeyPress(e, 'quantity', index)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-500">Price: ₹{med.pricePerUnit} | GST: {med.gstPercentage}% | Disc: {med.discountPercentage}%</div>
                                                <div className="flex justify-between items-center bg-teal-50 px-4 py-3 rounded-lg">
                                                    <span className="font-semibold text-gray-700">Total:</span>
                                                    <span className="font-bold text-teal-700 text-lg">₹{med.totalPrice.toFixed(2)}</span>
                                                </div>
                                                {med.hasReturns && (
                                                    <div className="mt-2 text-xs px-2">
                                                        <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 font-bold rounded-full mr-2">-{med.returnedQuantity} Returned</span>
                                                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 font-bold rounded-full">= {med.netQuantity} Net</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={addMedicine}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addMedicine();
                                            }
                                        }}
                                        className="flex items-center space-x-2 px-4 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="font-semibold">Add Medicine</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Payment & Summary */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-teal-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Payment Details
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Mode *</label>
                                        <select
                                            name="paymentMode"
                                            className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                            value={formData.paymentMode}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Mode</option>
                                            <option value="cash">Cash</option>
                                            <option value="online">Online</option>
                                            <option value="cash_and_online">Cash + Online</option>
                                            <option value="card">Card</option>
                                            <option value="debits">Debits</option>
                                        </select>
                                        {errors.paymentMode && <p className="text-red-500 text-xs mt-1.5">{errors.paymentMode}</p>}
                                    </div>
                                    {formData.paymentMode === 'cash_and_online' && (
                                        <div className="space-y-4 bg-teal-50/50 p-4 rounded-xl border border-teal-100">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Cash Amount</label>
                                                <input
                                                    type="number"
                                                    name="cashAmount"
                                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                                    value={formData.cashAmount}
                                                    onChange={handleChange}
                                                    onKeyDown={handleEnterKey}
                                                />
                                                {errors.cashAmount && <p className="text-red-500 text-xs mt-1.5">{errors.cashAmount}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Online Amount</label>
                                                <input
                                                    type="number"
                                                    name="onlineAmount"
                                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                                                    value={formData.onlineAmount}
                                                    onChange={handleChange}
                                                    onKeyDown={handleEnterKey}
                                                />
                                                {errors.onlineAmount && <p className="text-red-500 text-xs mt-1.5">{errors.onlineAmount}</p>}
                                            </div>
                                        </div>
                                    )}
                                    {formData.paymentMode === 'debits' && (
                                        <div className="bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Debits Amount</label>
                                            <input
                                                type="number"
                                                name="debitsAmount"
                                                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                                                value={formData.debitsAmount}
                                                onChange={handleChange}
                                                onKeyDown={handleEnterKey}
                                            />
                                            {errors.debitsAmount && <p className="text-red-500 text-xs mt-1.5">{errors.debitsAmount}</p>}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-teal-100 rounded-2xl shadow-lg p-4 sm:p-6 border-2 border-teal-200">
                                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                    <div className="w-1 h-6 bg-teal-600 rounded-full mr-3"></div>
                                    Amount Summary
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-gray-600 font-medium">Subtotal (Before GST)</span>
                                        <span className="font-bold text-gray-800">₹{medicines.reduce((s, m) => s + ((m.basePrice || 0) * (m.quantity || 0)), 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-teal-600 font-medium text-sm">Total GST</span>
                                        <span className="text-teal-700 font-bold">+₹{medicines.reduce((s, m) => s + (m.gstAmount || 0), 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl">
                                        <span className="text-orange-600 font-medium text-sm">Total Discount</span>
                                        <span className="text-orange-700 font-bold">-₹{medicines.reduce((s, m) => s + (m.discountAmount || 0), 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-3 bg-white rounded-xl border-2 border-teal-200">
                                        <span className="text-gray-700 font-semibold">Total Amount</span>
                                        <span className="text-teal-700 font-bold text-lg">₹{totalAmount.toFixed(2)}</span>
                                    </div>
                                    <div className="pt-3 border-t-2 border-teal-200">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-sm font-semibold text-gray-700">Rounded Adjustment</label>
                                            <span className="text-xs text-teal-600 font-medium">Auto-rounded to nearest ₹10</span>
                                        </div>
                                        <input
                                            type="number"
                                            name="round_amount"
                                            step="0.01"
                                            className="w-full px-4 py-2.5 border-2 border-amber-300 rounded-xl bg-amber-50 font-bold text-amber-800 focus:ring-2 focus:ring-amber-500 outline-none"
                                            value={formData.round_amount}
                                            onChange={handleChange}
                                            onKeyDown={handleEnterKey}
                                            onFocus={() => setIsManualRound(true)}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">e.g. -2 → ₹250, +3 → ₹260</p>
                                    </div>

                                    <div className="pt-3">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Net Amount *</label>
                                        <div className="w-full px-4 py-3 bg-teal-600 text-white font-bold text-xl rounded-xl text-center shadow-lg">
                                            ₹{formData.netAmount || '0.00'}
                                        </div>
                                    </div>
                                    <div className="pt-3 border-t-2 border-teal-200">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Net Amount *</label>
                                        <input
                                            type="number"
                                            name="netAmount"
                                            className="w-full px-4 py-3 border-2 border-teal-300 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white font-bold text-lg text-teal-700 transition-all"
                                            value={formData.netAmount}
                                            onChange={handleChange}
                                            onKeyDown={handleEnterKey}
                                        />
                                        {errors.netAmount && <p className="text-red-500 text-xs mt-1.5">{errors.netAmount}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end mt-8 space-x-4">
                            {/* Cancel Button */}
                            <button
                                type="button"
                                onClick={() => navigate('/invoice')}
                                className="flex items-center space-x-2 px-8 py-3.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-bold"
                            >
                                <span>Cancel</span>
                            </button>

                            {/* Existing Save Button */}
                            <button
                                type="submit"
                                className="flex items-center space-x-2 px-8 py-3.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                                disabled={!medicines.length || !medicines.every(isRowValid)}
                            >
                                <Save className="h-5 w-5" />
                                <span>Save Invoice</span>
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </Authenticated>
    );
};

export default EditInvoice;